/** @file drawnAzimuthDefaults: per drawn tile, the azimuth an entry assumes without `drawnAzimuths` (T-327). */
import { tileCountOf } from '@zilo/pose3d-format/tables/tileCountOf';
import type { SectorTable } from '@zilo/pose3d-format/types/facet';

/** One tile: the sectors drawing it as drawn and mirrored, its default azimuth (null: only mirrored) and a suggestion. */
export type TileAzimuth = { tile: number; sectors: number[]; flippedIn: number[]; defaultDrawnAzimuth: number | null; suggestedDrawnAzimuth: number };

const wrap = (deg: number): number => Number((((deg % 360) + 360) % 360).toFixed(4));

/**
 * The default is what `slots` assumes (capture's `nominalAzimuthOfTile`: `s * 360 / n` for the first sector `s` that
 * draws the tile unmirrored); a tile only ever drawn mirrored has none, so an entry on that table must list
 * `drawnAzimuths`. The suggestion is the default, else the mirror (`360 - s * 360 / n`) of its first mirrored sector.
 */
export function drawnAzimuthDefaults(table: SectorTable): TileAzimuth[] {
  const n = table.tile.length;
  return Array.from({ length: tileCountOf(table) }, (_, tile): TileAzimuth => {
    const sectors = table.tile.flatMap((t, s) => (t === tile && table.flip[s] !== true ? [s] : []));
    const flippedIn = table.tile.flatMap((t, s) => (t === tile && table.flip[s] === true ? [s] : []));
    const first = sectors[0];
    const defaultDrawnAzimuth = first === undefined ? null : (first * 360) / n;
    return { tile, sectors, flippedIn, defaultDrawnAzimuth, suggestedDrawnAzimuth: defaultDrawnAzimuth ?? wrap(360 - ((flippedIn[0] ?? 0) * 360) / n) };
  });
}
